import { Link } from "react-router-dom";
import { Check, Minus, Sparkles, ArrowRight } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/motion";
import { CTAPanel } from "../components/CTAPanel";
import { PRICING, GROWTH_PACKAGES, AUTOMATE_PACKAGES, CARE_PLANS, ADDONS } from "../data/content";

const COMPARISON = [
  { label: "Custom website", values: ["3 pages", "Up to 8 pages", "Up to 12 pages"] },
  { label: "Mobile-friendly & fast", values: [true, true, true] },
  { label: "Domain & business email setup", values: [true, true, true] },
  { label: "Basic SEO setup", values: [true, true, true] },
  { label: "Logo & brand mini-kit", values: [false, true, true] },
  { label: "Booking system or payments", values: [false, true, true] },
  { label: "Social media setup + content", values: [false, "2 weeks", "4 weeks"] },
  { label: "AI chatbot", values: [false, false, true] },
  { label: "Payment gateway integration", values: [false, false, true] },
  { label: "Priority support", values: [false, false, "30 days"] },
  { label: "Hosting & maintenance", values: ["$69/month", "$99/month", "Free year 1, then $99/mo"] },
  { label: "Typical turnaround", values: ["~1 week", "~2 weeks", "~3 weeks"] },
];

const Cell = ({ v, featured }) => {
  if (v === true) return <Check className="w-4 h-4 mx-auto text-indigo" />;
  if (v === false) return <Minus className="w-4 h-4 mx-auto text-slate/40" />;
  return <span className={`text-xs font-medium ${featured ? "text-indigo font-semibold" : "text-ink"}`}>{v}</span>;
};

const ComparisonTable = ({ tiers, rows, testid }) => (
  <Reveal>
    <div className="bg-white rounded-[2rem] border border-ink/5 shadow-sm overflow-hidden overflow-x-auto mt-10">
      <table className="w-full min-w-[560px] text-sm" data-testid={testid}>
        <thead>
          <tr className="border-b border-ink/5">
            <th className="text-left font-display font-semibold text-ink p-5">Feature</th>
            {tiers.map((t, i) => (
              <th key={t.name} className={`p-5 font-display font-semibold ${i === 1 ? "text-indigo" : "text-ink"}`}>
                {t.name}
                <span className="block text-xs font-body font-normal text-slate mt-0.5">{t.price}/mo</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.label} className={i % 2 === 0 ? "bg-cloud/60" : ""}>
              <td className="p-5 text-ink/75">{row.label}</td>
              {row.values.map((v, j) => (
                <td key={j} className="p-5 text-center"><Cell v={v} featured={j === 1} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Reveal>
);

const MonthlySection = ({ id, label, title, sub, packages, testid, planPrefix = "", comparison }) => (
  <section id={id} className="px-5 sm:px-8 pb-24" data-testid={testid}>
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <p className="text-indigo font-semibold text-sm uppercase tracking-widest mb-4">{label}</p>
        <h2 className="font-display font-bold text-2xl sm:text-3xl text-ink mb-3">{title}</h2>
        <p className="text-slate text-sm sm:text-base max-w-2xl mb-10">{sub}</p>
      </Reveal>
      <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
        {packages.map((tier) => (
          <StaggerItem key={tier.name} className="h-full">
            <div
              data-testid={`${testid}-tier-${tier.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`relative h-full rounded-[2rem] p-8 flex flex-col ${
                tier.featured
                  ? "bg-ink text-white shadow-[0_24px_64px_rgba(20,22,46,0.3)] md:-translate-y-3"
                  : "bg-white border border-ink/5 shadow-sm"
              }`}
            >
              {tier.featured && (
                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-indigo to-mint text-white text-xs font-semibold px-4 py-1.5 rounded-full inline-flex items-center gap-1.5 whitespace-nowrap">
                  <Sparkles className="w-3 h-3" /> Most popular
                </span>
              )}
              <h3 className="font-display font-semibold text-lg">{tier.name}</h3>
              <div className="mt-3 flex items-baseline gap-1.5">
                <span className="font-display font-bold text-4xl">{tier.price}</span>
                <span className={`text-xs ${tier.featured ? "text-white/50" : "text-slate"}`}>/month AUD</span>
              </div>
              <p className={`text-sm mt-2 ${tier.featured ? "text-white/60" : "text-slate"}`}>{tier.tagline}</p>
              <ul className="mt-7 space-y-3 flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${tier.featured ? "text-mint" : "text-indigo"}`} />
                    <span className={tier.featured ? "text-white/80" : "text-ink/75"}>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={`/contact?plan=${encodeURIComponent(`${planPrefix}${tier.name} (monthly)`)}`}
                data-testid={`${testid}-cta-${tier.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`mt-8 inline-flex items-center justify-center gap-2 font-semibold px-6 py-3 rounded-full transition-colors duration-300 ${
                  tier.featured
                    ? "bg-gradient-to-r from-indigo to-mint text-white hover:opacity-90"
                    : "bg-ink text-white hover:bg-indigo"
                }`}
              >
                {tier.cta} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
      {comparison && <ComparisonTable tiers={packages} rows={comparison} testid={`${testid}-comparison-table`} />}
    </div>
  </section>
);

const GROW_COMPARISON = [
  { label: "Monthly content shoot", values: ["2-hour", "5 hours", "8 hours"] },
  { label: "Edited photos", values: ["10", "25", "Unlimited"] },
  { label: "Reels", values: ["4", "10", "20"] },
  { label: "Social media posts", values: ["8", "12", "20"] },
  { label: "Content scheduling", values: [true, true, true] },
  { label: "Caption writing", values: [false, true, true] },
  { label: "Social media management", values: [false, true, true] },
  { label: "Google Business updates", values: [false, true, true] },
  { label: "Drone footage", values: [false, false, true] },
  { label: "Staff & customer interviews", values: [false, false, true] },
  { label: "TikTok & YouTube Shorts", values: [false, false, true] },
  { label: "Website & AI chatbot updates", values: [false, false, true] },
  { label: "Monthly reporting", values: ["Performance report", "Strategy meeting", "Analytics & strategy"] },
];

const AUTOMATE_COMPARISON = [
  { label: "AI chatbot", values: ["Monitoring", true, "Custom solutions"] },
  { label: "CRM", values: ["Basic", "Management", "Integrations"] },
  { label: "Lead automation", values: [false, true, true] },
  { label: "Appointment automation", values: [false, true, true] },
  { label: "Workflow optimisation", values: [false, true, "Multi-step"] },
  { label: "API connections", values: [false, false, true] },
  { label: "Dashboards", values: [false, false, true] },
  { label: "Support", values: ["Email", "Monthly reporting", "Priority"] },
];

export default function Pricing() {
  return (
    <>
      <section className="pt-[72px] hero-grid-bg" data-testid="pricing-hero">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-16 sm:pt-28 sm:pb-20">
          <Reveal>
            <p className="text-indigo font-semibold text-sm uppercase tracking-widest mb-4">Pricing</p>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-ink max-w-3xl leading-[1.06]">
              Fixed launch prices. <span className="text-gradient">Simple monthly plans.</span>
            </h1>
            <p className="mt-6 text-slate text-base sm:text-lg max-w-2xl leading-relaxed">
              Get online with a one-off launch package, then keep the momentum with monthly growth, automation and care plans. All prices in AUD — no lock-ins, no surprises.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {[
                ["#launch", "Launch Packages"],
                ["#grow", "Grow Monthly"],
                ["#automate", "Automate Monthly"],
                ["#care", "Website Care"],
                ["#addons", "Add-ons"],
              ].map(([href, label]) => (
                <a
                  key={href}
                  href={href}
                  data-testid={`pricing-jump-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="inline-flex items-center bg-white border border-ink/10 rounded-full px-5 py-2.5 text-sm font-semibold text-ink hover:border-indigo hover:text-indigo transition-colors duration-200"
                >
                  {label}
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section id="launch" className="px-5 sm:px-8 pb-20" data-testid="pricing-tiers">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <p className="text-indigo font-semibold text-sm uppercase tracking-widest mb-4">Launch — one-off packages</p>
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-ink mb-10">Get online, fixed price</h2>
          </Reveal>
        </div>
        <Stagger className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
          {PRICING.map((tier) => (
            <StaggerItem key={tier.name} className="h-full">
              <div
                data-testid={`pricing-tier-${tier.name.toLowerCase()}`}
                className={`relative h-full rounded-[2rem] p-9 flex flex-col ${
                  tier.featured
                    ? "bg-ink text-white shadow-[0_24px_64px_rgba(20,22,46,0.3)] md:-translate-y-3"
                    : "bg-white border border-ink/5 shadow-sm"
                }`}
              >
                {tier.featured && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-indigo to-mint text-white text-xs font-semibold px-4 py-1.5 rounded-full inline-flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" /> Most popular
                  </span>
                )}
                <h2 className="font-display font-semibold text-xl">{tier.name}</h2>
                <div className="mt-3 flex items-baseline gap-1.5">
                  <span className="font-display font-bold text-5xl">${tier.price.toLocaleString()}</span>
                  <span className={`text-xs ${tier.featured ? "text-white/50" : "text-slate"}`}>AUD, one-off</span>
                </div>
                <p className={`text-sm mt-2 ${tier.featured ? "text-white/60" : "text-slate"}`}>{tier.tagline}</p>
                <p className={`text-xs font-semibold mt-4 inline-flex items-center gap-1.5 ${tier.featured ? "text-mint" : "text-indigo"}`}>
                  {tier.turnaround}
                </p>
                <ul className="mt-7 space-y-3 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${tier.featured ? "text-mint" : "text-indigo"}`} />
                      <span className={tier.featured ? "text-white/80" : "text-ink/75"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/contact?plan=${encodeURIComponent(`${tier.name} Package`)}`}
                  data-testid={`pricing-tier-cta-${tier.name.toLowerCase()}`}
                  className={`mt-8 inline-flex items-center justify-center gap-2 font-semibold px-6 py-3.5 rounded-full transition-colors duration-300 ${
                    tier.featured
                      ? "bg-gradient-to-r from-indigo to-mint text-white hover:opacity-90"
                      : "bg-ink text-white hover:bg-indigo"
                  }`}
                >
                  {tier.cta} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      <section className="px-5 sm:px-8 pb-24" data-testid="pricing-comparison">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-ink mb-8">Compare every feature</h2>
          </Reveal>
          <Reveal>
            <div className="bg-white rounded-[2rem] border border-ink/5 shadow-sm overflow-hidden overflow-x-auto">
              <table className="w-full min-w-[560px] text-sm" data-testid="pricing-comparison-table">
                <thead>
                  <tr className="border-b border-ink/5">
                    <th className="text-left font-display font-semibold text-ink p-5">Feature</th>
                    {PRICING.map((t) => (
                      <th key={t.name} className={`p-5 font-display font-semibold ${t.name === "Growth" ? "text-indigo" : "text-ink"}`}>
                        {t.name}
                        <span className="block text-xs font-body font-normal text-slate mt-0.5">${t.price.toLocaleString()}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON.map((row, i) => (
                    <tr key={row.label} className={i % 2 === 0 ? "bg-cloud/60" : ""}>
                      <td className="p-5 text-ink/75">{row.label}</td>
                      {row.values.map((v, j) => (
                        <td key={j} className="p-5 text-center"><Cell v={v} featured={j === 1} /></td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      <MonthlySection
        id="grow"
        testid="growth-packages"
        label="Grow — monthly packages"
        title="Content that keeps customers coming"
        sub="Professional shoots, reels, posts and ads management — a full marketing team for a fraction of the cost."
        packages={GROWTH_PACKAGES}
        comparison={GROW_COMPARISON}
      />

      <MonthlySection
        id="automate"
        testid="automate-packages"
        label="Automate — monthly packages"
        title="AI and automation that pays for itself"
        sub="Chatbots, CRM and workflows that quietly handle the admin while you do the work that makes money."
        packages={AUTOMATE_PACKAGES}
        comparison={AUTOMATE_COMPARISON}
      />

      <MonthlySection
        id="care"
        testid="care-plans"
        label="Website Care Plans — monthly"
        title="We keep your website healthy"
        sub="Security, updates, backups and content changes — so your site stays fast, safe and current without you thinking about it."
        packages={CARE_PLANS}
        planPrefix="Care Plan "
      />

      <section id="addons" className="px-5 sm:px-8 pb-24" data-testid="pricing-addons">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <p className="text-indigo font-semibold text-sm uppercase tracking-widest mb-4">Add-ons</p>
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-ink mb-10">Bolt on exactly what you need</h2>
          </Reveal>
          <Reveal>
            <div className="bg-white rounded-[2rem] border border-ink/5 shadow-sm overflow-hidden">
              <table className="w-full text-sm" data-testid="addons-table">
                <tbody>
                  {ADDONS.map((a, i) => (
                    <tr
                      key={a.name}
                      data-testid={`addon-${a.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className={`${i % 2 === 0 ? "bg-cloud/60" : ""} hover:bg-indigo/5 transition-colors duration-150`}
                    >
                      <td className="p-5 text-ink/80 font-medium">{a.name}</td>
                      <td className="p-5 text-right font-display font-bold text-indigo whitespace-nowrap">{a.price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      <CTAPanel title="Not sure which package fits?" sub="Book a free 15-minute chat. We'll tell you honestly what you need — even if that's the cheapest option." />
    </>
  );
}
